import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  CircularProgress,
  Breadcrumbs,
  Link,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Image as ImageIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { spacesService } from '../services/spaces.service';
import diagramsService from '../services/diagrams.service';
import type { Diagram } from '../../../shared/src/types/space.types';

// Local form type for the dialog
interface DiagramFormData {
  name: string;
  description: string;
}

export const SpaceDetail: React.FC = () => {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Check if we should auto-open the create dialog (from ?action=create-diagram)
  const shouldOpenCreateDialog =
    searchParams.get('action') === 'create-diagram';

  const [dialogOpen, setDialogOpen] = useState(shouldOpenCreateDialog);
  const [editingDiagram, setEditingDiagram] = useState<Diagram | null>(null);
  const [formData, setFormData] = useState<DiagramFormData>({
    name: '',
    description: '',
  });

  // Clear the action param once dialog is shown
  useEffect(() => {
    if (shouldOpenCreateDialog) {
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, [shouldOpenCreateDialog, searchParams, setSearchParams]);

  // Fetch space details
  const { data: space, isLoading: spaceLoading } = useQuery({
    queryKey: ['spaces', spaceId],
    queryFn: () => spacesService.getById(spaceId || ''),
    enabled: !!spaceId,
  });

  // Fetch diagrams for this space
  const { data: diagrams, isLoading: diagramsLoading } = useQuery<Diagram[]>({
    queryKey: ['diagrams', spaceId],
    queryFn: async () => {
      if (!spaceId) return [];
      return diagramsService.getAll(spaceId);
    },
    enabled: !!space,
  });

  const createDiagramMutation = useMutation({
    mutationFn: async (data: DiagramFormData) => {
      if (!spaceId) throw new Error('Space ID required');
      return diagramsService.create({
        spaceId,
        name: data.name,
        description: data.description || undefined,
        diagramData: {
          nodes: [],
          edges: [],
          canvasState: { zoom: 1, offsetX: 0, offsetY: 0 },
        },
      });
    },
    onSuccess: newDiagram => {
      queryClient.invalidateQueries({ queryKey: ['diagrams', spaceId] });
      handleCloseDialog();
      // Navigate to diagram builder
      navigate(`/spaces/${spaceId}/diagrams/${newDiagram.id}`);
    },
  });

  const deleteDiagramMutation = useMutation({
    mutationFn: async (diagramId: string) => {
      return diagramsService.delete(diagramId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagrams', spaceId] });
    },
  });

  const handleOpenDialog = (diagram?: Diagram) => {
    if (diagram) {
      setEditingDiagram(diagram);
      setFormData({
        name: diagram.name,
        description: diagram.description || '',
      });
    } else {
      setEditingDiagram(null);
      setFormData({
        name: '',
        description: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDiagram(null);
  };

  const handleSubmit = () => {
    if (editingDiagram) {
      // TODO: Update existing diagram when API is ready
      // Placeholder for diagram update
      handleCloseDialog();
    } else {
      createDiagramMutation.mutate(formData);
    }
  };

  const handleDeleteDiagram = (diagramId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((globalThis as any).confirm(t('diagram.deleteConfirm'))) {
      deleteDiagramMutation.mutate(diagramId);
    }
  };

  const handleOpenDiagram = (diagramId: string) => {
    navigate(`/spaces/${spaceId}/diagrams/${diagramId}`);
  };

  if (spaceLoading || diagramsLoading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (!space) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h5">{t('space.notFound')}</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/spaces')}
          sx={{ mt: 2 }}
        >
          {t('common.back')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/spaces')}
          sx={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          {t('nav.spaces')}
        </Link>
        <Typography color="text.primary">{space.name}</Typography>
      </Breadcrumbs>

      {/* Space Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {space.name}
          </Typography>
          {space.description && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {space.description}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={space.isPublic ? t('space.public') : t('space.private')}
              color={space.isPublic ? 'primary' : 'default'}
              size="small"
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={() => navigate(`/spaces/${spaceId}/settings`)}
            title={t('space.settings')}
          >
            <SettingsIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            {t('diagram.create')}
          </Button>
        </Box>
      </Box>

      {/* Diagrams Grid */}
      {!diagrams || diagrams.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <ImageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('diagram.noDiagrams')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('diagram.noDiagramsDescription')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            {t('diagram.createFirst')}
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {diagrams.map((diagram: Diagram) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={diagram.id}>
              <Card>
                <CardMedia
                  sx={{
                    height: 200,
                    backgroundColor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleOpenDiagram(diagram.id)}
                >
                  {diagram.thumbnail ? (
                    <Box
                      component="img"
                      src={`data:image/png;base64,${diagram.thumbnail}`}
                      alt={diagram.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <ImageIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                  )}
                </CardMedia>
                <CardContent>
                  <Typography variant="h6" component="div" noWrap>
                    {diagram.name}
                  </Typography>
                  {diagram.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {diagram.description}
                    </Typography>
                  )}
                  <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                    <Chip
                      label={`${diagram.diagramData?.nodes?.length || 0} ${t('diagram.components')}`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`${diagram.diagramData?.edges?.length || 0} ${t('diagram.relations')}`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    onClick={() => handleOpenDiagram(diagram.id)}
                  >
                    {t('diagram.open')}
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(diagram)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteDiagram(diagram.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Diagram Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingDiagram ? t('diagram.edit') : t('diagram.create')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('diagram.name')}
            fullWidth
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <TextField
            margin="dense"
            label={t('diagram.description')}
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={e =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || createDiagramMutation.isPending}
          >
            {editingDiagram ? t('common.save') : t('diagram.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
